import React, { useEffect, useState } from 'react';
import { PageLink, PageTitle } from '../../layout/core';
import { useLocation } from 'react-router';
import { GoBackBtn } from '../header/GoBackBtn';
import { OrderInformation } from './OrderInformation';
import { CustomerInformation } from './CustomerInformation';
import { OrderItem } from './OrderItem';
import { PaymentActivity } from './PaymentActivity';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '../Spinner';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { BUTTON_TYPE, ButtonComponent } from '../ButtonComponent';
import './OrderDetails.scss';
import { IAuthState } from '../../redux/AuthRedux';
import { ADMIN_GROUPS } from '../../views/login/Login';
import { fetchBlockList } from '../../store/eligibility/actions/order-block';
import { useEligibilityContext } from '../../store/eligibility/eligibility-context';
import { PaymentInstrument } from './PaymentInstrument';
import { OrderStatus } from './OrderStatus';
import { AdditionalHeaderButtons } from './AdditionalHeaderButtons';
import { useOrdersContext } from '../../store/order/order-context';
import {
	fetchOrderDetailsData,
	setOrderDetailsDataToDefault,
} from '../../store/order/actions/order';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { fetchUser } from '../../store/identyti/actions/users';
import { useIdentity } from '../../store/identyti/identity-context';
import { usePaymentContext } from '../../store/payments/payments-context';
import { fetchDisputeTypes } from '../../store/payments/actions/disputes';
import { fetchPaymentInstrumentsData } from '../../store/payments/actions/order';
import { fetchDisputesManagementSystems } from '../../store/disputes/actions/disputes';
import { useDisputesContext } from '../../store/disputes/disputes-context';

const orderBreadCrumbs: Array<PageLink> = [
	{
		title: 'ORDERS.ORDERS',
		path: '/orders',
		isSeparator: false,
		isActive: false
	},
	{
		title: '',
		path: '',
		isSeparator: true,
		isActive: false
	}
];

export interface IOrderItem {
	amount?: number;
	discount?: number;
	id?: string;
	name?: string;
	quantity?: number;
	referenceId?: string;
	tax?: number;
	unitPrice?: number;
}

export interface IBillingAddress {
	line1?: string;
	line2?: string;
	city?: string;
	countryCode?: number;
}

export interface IActivityItem {
	description?: string;
	orderId?: string;
	paymentDate?: string | Date;
	paymentTransactionId?: string;
	totalAmount?: number;
	totalAmountFormatted?: null | string | number;
	type?: string;
}

export interface IOrderHateoasLinks {
	href: string;
	rel: string;
	method: string;
	category: string;
	actionName: string;
	description: string;
	fields: null;
}

export interface IOrder {
	activityItems?: IActivityItem[];
	billingAddress?: IBillingAddress;
	orderItems?: IOrderItem[];
	currencySymbol?: string;
	createdOn?: Date;
	totalBalancePaid?: number;
	balanceToBePaid?: number;
	totalBalancePaidFormatted?: null | string | number;
	balanceToBePaidFormatted?: null | string | number;
	currency?: string;
	currencyCode?: string;
	amount?: number;
	formattedAmount?: string;
	statusName?: string;
	payoutStatus?: string | null;
	isGhostMode?: boolean;
	referenceNumber?: string;
	orderPlacedDate?: Date | string | number;
	firstName?: string;
	lastName?: string;
	phone?: string;
	email?: string;
	paymentInstrumentToken?: string;
	merchantName?: string;
	siteName?: string;
	siteDescriptor?: string;
	merchantId?: string;
	isCancelable?: boolean;
	'_links'?: IOrderHateoasLinks[];
}

export const OrderDetails = (): JSX.Element => {
	const { pathname } = useLocation();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const isSuperAdmin = authUser?.userData?.userGroups?.some(el => el === ADMIN_GROUPS.SUPER_ADMIN);
	const email = authUser?.userData?.email;

	const { id: orderId } = useParams<{ id: string; type: string }>();
	const pageName = pathname.split('/')[1];

	const { orderContextState: { orderDetailsData: order, isOrderDetailsDataLoading }, dispatch } = useOrdersContext();
	const { dispatch: eligibilityDispatch } = useEligibilityContext();
	const { dispatch: paymentDispatch } = usePaymentContext();
	const { identityState: { isUserDataLoading }, dispatch: identityDispatch } = useIdentity();

	const { disputesContextState, dispatch: disputesDispatch } = useDisputesContext();

	const [actionLinks, setActionLinks] = useState<IOrderHateoasLinks[]>([]);

	const { t } = useTranslation();
	const navigate = useNavigate();

	const fetchDetailsData = async (): Promise<void> => {
		const links = await fetchOrderDetailsData(dispatch, orderId);
		setActionLinks(links);
	};

	useEffect(() => {
		void fetchDetailsData();
	}, []);

	useEffect(() => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN)) {
			void fetchDisputesManagementSystems(disputesDispatch);
		}
	}, []);

	useEffect(() => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_MERCHANT_ADMIN_AND_PARTNER_ADMIN)) {
			void fetchBlockList(eligibilityDispatch, orderId);
		}
	}, []);

	useEffect(() => {
		if (isSuperAdmin) {
			void fetchDisputeTypes(paymentDispatch);
		}
	}, []);

	useEffect(() => {
		if (order?.paymentInstrumentToken) {
			void fetchPaymentInstrumentsData({ dispatch: paymentDispatch, token: order?.paymentInstrumentToken
		});
		}
	}, [order]);

	useEffect(() => {
		void fetchUser(identityDispatch, email );
	}, []);

	useEffect(() => {
		return () => {
			setOrderDetailsDataToDefault(dispatch);
		};
	}, []);

	if (isOrderDetailsDataLoading || isUserDataLoading) {
		return (
			<div className="card w-100 h-100">
				<div className="card-body">
					<Spinner
						pageLoader={true}
					/>
				</div>
			</div>
		);
	}

	if (!order) {
		return (
			<>
				<PageTitle breadcrumbs={orderBreadCrumbs}>{orderId}</PageTitle>
				<GoBackBtn path={history.length > 2 ? null : '/orders'} />
				<div className="card mt-10 position-relative">
					<div className="card-body no-data-page fw-bolder fs-4 ">
						<div className="position-absolute text">
							No data was found
						</div>
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<PageTitle breadcrumbs={orderBreadCrumbs}>{orderId}</PageTitle>
				<>
					<div className="card bg-transparent mb-10">
						<div className="d-flex justify-content-between border-bottom-0 align-items-center">
							<GoBackBtn />
							<div className="d-flex align-items-center">

								<OrderStatus statusName={order?.payoutStatus} title={t('ORDERS.PAYOUT_STATUS')}/>
								<OrderStatus statusName={order?.statusName} title={t('ORDERS.STATUS')}/>

								<AdditionalHeaderButtons actionLinks={actionLinks} />

							</div>

						</div>
					</div>

					<div className="row">
						<div className="col-12 col-md-4">

							<OrderInformation/>

							<CustomerInformation/>

							<PaymentInstrument/>

						</div>

						<div className="col-12 col-md-8">
							<OrderItem
								orderId={orderId}
								pageName={pageName}
								actionLinks={actionLinks}
							/>
							<PaymentActivity/>

						</div>
					</div>
					{
						isSuperAdmin && (
							<>
								<ButtonComponent
									buttonType={BUTTON_TYPE.WHITE}
									buttonName={t('ORDERS.ACTIVITY_LOG_BTN_NAME')}
									additionalClass="mt-5 d-block d-md-none w-100 w-sm-400px mx-auto"
									onButtonClick={(): void => navigate(`${pathname}/activity`,
										{ state: { isGhostMode: order?.isGhostMode }})}
								/>
							</>
						)
					}
				</>
		</>
	);
};

